import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Helper function to detect if content is a URL
const isUrl = (str) => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

export default function BlogPost() {
  const { id } = useParams();
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const progressRef = useRef(null);

  useEffect(() => {
    // Load email from localStorage on mount
    const savedEmail = localStorage.getItem("blog_email");
    if (savedEmail) {
      setEmail(savedEmail);
    }

    // Reading progress tracker — update DOM directly to avoid re-renders
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = Math.min((scrolled / documentHeight) * 100, 100);
      if (progressRef.current) {
        progressRef.current.style.width = `${progress}%`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setLoading(true);
        const savedEmail = localStorage.getItem("blog_email");

        // If email exists, fetch with email to get full content
        if (savedEmail) {
          const res = await axios.get(`${API_BASE}blogs/${id}`, {
            params: { email: savedEmail },
          });
          setBlog(res.data);
        } else {
          // Otherwise just fetch preview
          const res = await axios.get(`${API_BASE}blogs/${id}`);
          // const paragraphs = res.data.summary.split(/\n\s*\n/);
          // res.data.summary = paragraphs
          // console.log(paragraphs)
          setBlog(res.data);
        }
      } catch (e) {
        console.error(e);
        setError("Could not load blog");
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [API_BASE, id]);

  const requestFull = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}blogs/${id}`, {
        params: { email },
      });
      setBlog(res.data);
      // Save email to localStorage for future visits
      localStorage.setItem("blog_email", email);
      setShowEmailModal(false);

      // Auto-trigger download after email submission
      if (res.data?.content && isUrl(res.data.content)) {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = res.data.content;
          a.download = `${(res.data.title || "blog").replace(/[^a-z0-9]/gi, "_")}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }, 500);
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Failed to fetch full blog");
    } finally {
      setLoading(false);
    }
  };

  const downloadContent = () => {
    if (!email) {
      setShowEmailModal(true);
      return;
    }
    if (!blog?.content) return;

    // If content is a URL, download the PDF file
    if (isUrl(blog.content)) {
      const a = document.createElement("a");
      a.href = blog.content;
      a.download = `${(blog.title || "blog").replace(/[^a-z0-9]/gi, "_")}.pdf`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // Otherwise download as text file (legacy content)
    const blob = new Blob([blog.content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(blog.title || "blog").replace(/[^a-z0-9]/gi, "_")}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading && !blog) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#EEF2F3] mb-4">
          <svg className="h-8 w-8 text-[#0060C7] animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-slate-600 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Loading article...</p>
      </div>
    </div>
  );
  if (error && !blog) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-50 mb-4">
          <svg className="h-8 w-8 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0-12a9 9 0 110 18 9 9 0 010-18z" />
          </svg>
        </div>
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    </div>
  );

  const showFullContent = email;

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700&display=swap');

        /* Reading Progress Bar */
        .reading-progress {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, #0060C7, #195B81);
          z-index: 9999;
          transition: width 0.1s ease-out;
        }

        /* Custom Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.96);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }

        /* Typography Styles */
        .blog-title {
          font-family: 'montserrat';
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: 0.02em;
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .blog-meta {
          font-family: 'Montserrat', sans-serif;
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s backwards;
        }

        .blog-summary {
          font-family: 'Inter', sans-serif;
          font-size: 1.125rem;
          line-height: 1.8;
          color: #1a1a1a;
          text-align : justify;
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s backwards;
        }

        .blog-image-container {
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s backwards;
          overflow: hidden;
        }

        .blog-image {
          transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .blog-image-container:hover .blog-image {
          transform: scale(1.02);
        }

        /* Download Button Styles */
        .download-btn {
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          font-family: 'Montserrat', sans-serif;
        }

        .download-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }

        .download-btn:hover::before {
          left: 100%;
        }

        .download-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px -8px rgba(0, 96, 199, 0.4);
        }

        /* Tag Style */
        .blog-tag {
          font-family: 'Montserrat', sans-serif;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          font-size: 0.75rem;
          transition: all 0.3s ease;
        }

        .blog-tag:hover {
          transform: translateY(-1px);
        }

        /* Email Modal */
        .modal-backdrop {
          backdrop-filter: blur(8px);
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          font-family: 'Inter', sans-serif;
        }

        .modal-input {
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
        }

        .modal-input:focus {
          box-shadow: 0 0 0 3px rgba(0, 96, 199, 0.1);
          border-color: #0060C7;
        }

        /* CTA Card */
        .cta-card {
          background: linear-gradient(135deg, #EEF2F3 0%, #E9EEFC 100%);
          border: 2px solid #E2EBF9;
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s backwards;
        }

        .cta-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #0060C7, #195B81, #23619A);
          border-radius: 12px 12px 0 0;
        }

        /* Decorative Elements */
        .decorative-line {
          position: relative;
          height: 1px;
          background: linear-gradient(90deg, transparent, #D8DDF2, transparent);
        }

        .decorative-line::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 6px;
          height: 6px;
          background: #0060C7;
          border-radius: 50%;
        }

        /* Back Link */
        .back-link {
          font-family: 'Montserrat', sans-serif;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .back-link:hover {
          transform: translateX(-4px);
          color: #0060C7;
        }

        .back-link svg {
          transition: transform 0.3s ease;
        }

        .back-link:hover svg {
          transform: translateX(-2px);
        }
      `}</style>

      {/* Reading Progress Bar */}
      <div
        ref={progressRef}
        className="reading-progress"
        style={{ width: "0%" }}
      />

      {/* Article Container */}
      <article className="w-full px-6 sm:px-[50px] py-12 sm:py-16">
        {/* Back Navigation */}
        <div className="max-w-6xl mx-auto mb-8">
          <Link to="/" className="back-link text-sm text-slate-600 hover:text-[#0060C7]">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Home
          </Link>
        </div>

        {/* Article Header */}
        <header className="mb-12 sm:mb-16 mt-8 max-w-6xl mx-auto">
          {/* Tag */}
          <div className="mb-6">
            <span className="blog-tag inline-block px-4 py-2 bg-[#EEF2F3] text-[#0060C7] rounded-md">
              {blog.tag}
            </span>
          </div>

          {/* Title */}
          <h1 className="blog-title text-4xl sm:text-5xl lg:text-6xl text-[#02033B] mb-8">
            {blog.title}
          </h1>

          {/* Meta Information */}
          <div className="blog-meta flex flex-wrap items-center gap-4 text-sm sm:text-base mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0060C7] to-[#195B81] flex items-center justify-center text-white font-semibold">
                {blog.author.charAt(0)}
              </div>
              <div>
                <p className="font-medium text-[#02033B]">{blog.author}</p>
                <p className="text-xs text-slate-500">{blog.date}</p>
              </div>
            </div>
            <span className="hidden sm:inline text-slate-300">|</span>
            <div className="flex items-center gap-2 text-slate-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">{blog.timeToRead} read</span>
            </div>
          </div>

          {/* Decorative Line */}
          <div className="decorative-line mb-8"></div>

          {/* Download Button - Desktop */}
          {blog.type !== "blog" && (
            <div className="hidden sm:block">
              <button
                onClick={downloadContent}
                className="download-btn px-8 py-3.5 bg-[#0060C7] text-white font-semibold rounded-lg flex items-center gap-3 hover:bg-[#195B81]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Full PDF
              </button>
            </div>
          )}
        </header>

        {/* Featured Image */}
        {blog.image_url && (
          <div className="blog-image-container max-w-6xl mx-auto mb-12 sm:mb-16 rounded-xl overflow-hidden">
            <img
              src={blog.image_url}
              alt={blog.title}
              className="blog-image w-full h-auto max-h-[500px] object-cover"
            />
          </div>
        )}

        {/* Article Body */}
        {blog.type === "blog" && blog.content ? (
          <div
            className="blog-summary prose prose-lg max-w-6xl mx-auto"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        ) : (
          <div className="blog-summary max-w-6xl mx-auto">
            {blog.summary
              ?.replace(/\.\n/g, ".\n\n")
              .split(/\n\n/)
              .map((para, i) => (
                <p key={i} className="mb-4">
                  {para.replace(/\n/g, " ").trim()}
                </p>
              ))}
          </div>
        )}

        {/* Decorative Line */}
        <div className="decorative-line my-12 sm:my-16 max-w-6xl mx-auto"></div>

        {/* CTA Section - Download PDF */}
        {blog.type !== "blog" && !showFullContent && (
          <div className="cta-card relative rounded-2xl p-8 sm:p-10 mb-12 max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-white shadow-md flex items-center justify-center">
                <svg className="w-7 h-7 text-[#0060C7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-[#02033B] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Download Complete Article
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Get the full PDF version delivered instantly. Enter your email once and access all our content seamlessly.
                </p>
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="download-btn px-8 py-3.5 bg-[#0060C7] text-white font-semibold rounded-lg inline-flex items-center gap-3 hover:bg-[#195B81]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Get Full PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Download Button - Mobile Fixed */}
        {blog.type !== "blog" && (
          <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-lg z-50">
            <button
              onClick={downloadContent}
              className="download-btn w-full px-6 py-3.5 bg-[#0060C7] text-white font-semibold rounded-lg flex items-center justify-center gap-3 hover:bg-[#195B81]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download PDF
            </button>
          </div>
        )}
      </article>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="modal-content bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#EEF2F3] mb-4">
                  <svg className="w-7 h-7 text-[#0060C7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#02033B] mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Unlock Full Article
                </h3>
                <p className="text-slate-600 text-sm">
                  Enter your email to download the complete PDF document
                </p>
              </div>

              <form onSubmit={requestFull} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#02033B] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="modal-input w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none"
                    placeholder="you@example.com"
                    required
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm font-medium">{error}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEmailModal(false)}
                    className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="download-btn flex-1 px-4 py-3 bg-[#0060C7] text-white font-semibold rounded-lg hover:bg-[#195B81] transition-colors"
                  >
                    Download
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
